import { UploadTask } from 'firebase/storage';
import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
import { SettingsService } from './settings.service';
import { ALLOWED_MODELS_EXTENSIONS, LOG_LEVEL_OPTION } from 'src/app/models/common';
import { RollbarService, CustomRollbar } from './rollbarlog.service';
import { FirebaseModelDto, ICard } from 'src/app/models/card';
import { IGroupedCard, UPLOAD_STATUS } from 'src/app/models/groupedCard';
import { ComponentType } from '@angular/cdk/overlay';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { ICardCollection } from 'src/app/models/collection';
import { environment } from 'src/environments/environment';
import { iconNotificationPopupData, PopupIconNotificationComponent } from '../components/popup-icon-notification/popup-icon-notification.component';

interface ImportantErrorOptions {
  timeout?: number;
  duration?: number;
  toastContext?: string;
  contextData?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class UtilityFunctionsService {

  public collectionDetailsMock: ICardCollection = {
    collectionId: undefined,
    collectionSharingId: undefined,
    collectionTitle: 'basic',
    collectionCards: [],
    accessType: 'Private',
    showFullscreenBackgroundPreview: false,
    preloadSettings: {
      enablePreload: true,
      numberOfCardsToPreload: 9,
    },
    created: new Date(Date.now()).toISOString(),
    updated: new Date(Date.now()).toISOString()
  }

  lastSnackBarRef: MatSnackBarRef<TextOnlySnackBar> | undefined;
  private readonly allowedSnackBarMessages = new Set([
    'Model sent for review',
    'Link copied to clipboard',
    'Your feedback has been sent successfully. We will reply shortly.',
  ]);

  constructor(public settingService: SettingsService,private _snackBar: MatSnackBar,public dialog: MatDialog,
    @Inject(RollbarService) private rollbar: CustomRollbar,
    ) { }

  public isSnackBarAllowed(text: string): boolean {
    return this.allowedSnackBarMessages.has(text);
  }

  private replacePopupTokens(value: string, replacements: Record<string, string>): string {
    let result = value;
    for (const [token, replacement] of Object.entries(replacements)) {
      result = result.split(token).join(replacement);
    }
    return result;
  }

  public applyPopupText<T extends Record<string, unknown>>(
    template: T,
    tokenOrMap?: string | Record<string, string>,
    value?: string
  ): T {
    if (!tokenOrMap) {
      return { ...template };
    }

    const replacements = typeof tokenOrMap === 'string'
      ? { [tokenOrMap]: value ?? '' }
      : tokenOrMap;

    const result: Record<string, unknown> = { ...template };
    for (const [key, fieldValue] of Object.entries(result)) {
      if (typeof fieldValue === 'string') {
        result[key] = this.replacePopupTokens(fieldValue, replacements);
      }
    }

    return result as T;
  }


  triggerHaptics(): void {
    if(this.settingService.devicePlatform === 'ios') {
      Haptics.impact({
        style: ImpactStyle.Light
      });
    }
  }

  async showShareDialog(text: string): Promise<void> {
    await Share.share({
      title: `Collaboration invite: ${text}`,
      text: `You've been invited for collaboration on ${text}`,
      url: 'http://3dway.io/',
      dialogTitle: `Collaboration invite 3DWay, ${text}`,
    });
  }

  public showSnackBar(text: string, timeout: number): void {
    if(this.settingService.logLevelSelector === LOG_LEVEL_OPTION.DEFAULT) {

      setTimeout(() => {
        const snackBarRef = this._snackBar.open(text, undefined, {
          horizontalPosition: 'start',
          verticalPosition: 'bottom',
          duration: 3000,
          panelClass: 'like-snackbar'
        });

        snackBarRef.afterOpened().subscribe(() => {
          // Store snackbar element in a variable
          const snackElement = document.querySelector('.like-snackbar');

          // Add click handler to the snackbar element
          if (snackElement) {
            snackElement.addEventListener('click', () => {
              snackBarRef.dismiss();
            });
          }
        });
      }, timeout);

    }
    if(this.settingService.logLevelSelector === LOG_LEVEL_OPTION.ERROR_ONLY || this.settingService.logLevelSelector === LOG_LEVEL_OPTION.PROD_MODE
      ) {
      console.log(text);
    }
  }

  public dissmissAllSnackBars(): void {
    console.log('dismissAllSnackBars')
    if(this.lastSnackBarRef) this.lastSnackBarRef.dismiss();
  }

  // force=true bypasses the allowedSnackBarMessages whitelist — use for dynamic
  // texts (e.g. names) that can't be whitelisted by exact match but must show.
  public showImportantSnackBar(text: string, timeout: number, duration: number = 3000, customClass?: string, force: boolean = false): void {
    this.rollbar.info(`${text} , uid: ${this.settingService.getOriginalUidFromLocalstorage()}`);
    if (!force && !this.isSnackBarAllowed(text)) {
      return;
    }

    setTimeout(() => {
      this.lastSnackBarRef = this._snackBar.open(text, undefined, {
        horizontalPosition: 'start',
        verticalPosition: 'bottom',
        duration,
        panelClass: ['like-snackbar', customClass].filter(Boolean)
      });

      this.attachDismissOnClick(this.lastSnackBarRef);
    }, timeout);
  }

  public getCurrentDateWithNanoseconds() {
    const now = new Date();
    // Get the standard ISO string without the 'Z' at the end: "2023-08-08T09:57:46.566"
    const isoString = now.toISOString().slice(0, -1);
    // Generate a random number to simulate nanoseconds (from 000000000 to 999999999)
    const nanoSeconds = Math.floor(Math.random() * 1000000000).toString().padStart(9, '0');
    return `${isoString}${nanoSeconds}Z`;
  }

  public showErrorSnackBar(text: string, timeout: number, customDuration: number = 3000): void {
    if(this.settingService.logLevelSelector !== LOG_LEVEL_OPTION.PROD_MODE) {
      setTimeout(() => {
        this._snackBar.open(text,undefined,{
          horizontalPosition: 'start',
          verticalPosition: 'bottom',
          duration: customDuration,
          panelClass: 'like-snackbar'
        })
      }, timeout);
    } else {
      const textWithUid = `${text} , uid: ${this.settingService.getOriginalUidFromLocalstorage()}`;
      console.error(textWithUid);
      this.rollbar.error(textWithUid);
    }
  }

  public showImportantErrorSnackBar(text: string, timeout: number, customDuration: number = 3000, additionalErrorData?: unknown): void {
    setTimeout(() => {
      this.lastSnackBarRef = this._snackBar.open(text,undefined,{
        horizontalPosition: 'start',
        verticalPosition: 'bottom',
        duration: customDuration,
        panelClass: 'like-snackbar'
      });

      this.attachDismissOnClick(this.lastSnackBarRef);
    }, timeout);

    const textWithUid = `${text} , uid: ${this.settingService.getOriginalUidFromLocalstorage()}`;
    const normalizedAdditionalData = this.safeSerialize(additionalErrorData);

    if (normalizedAdditionalData !== undefined) {
      console.error(textWithUid, normalizedAdditionalData);
    } else {
      console.error(textWithUid);
    }

    this.rollbar.error(textWithUid, normalizedAdditionalData);
  }

  public formatErrorForDisplay(
    error: unknown,
    fallback: string = 'Unable to complete the request. Please try again.',
  ): string {
    return this.extractErrorDiagnostics(error, fallback).displayMessage;
  }

  public importantError(
    title: string,
    error?: unknown,
    options: ImportantErrorOptions = {},
  ): string {
    const diagnostics = this.extractErrorDiagnostics(error);
    const toastText = this.truncateText(
      [title, options.toastContext, diagnostics.displayMessage].filter(Boolean).join(' | '),
      900,
    );
    const timeout = options.timeout ?? 0;
    const duration = options.duration ?? 12000;

    setTimeout(() => {
      this.lastSnackBarRef = this._snackBar.open(toastText, undefined, {
        horizontalPosition: 'start',
        verticalPosition: 'bottom',
        duration,
        panelClass: ['like-snackbar', 'text-center']
      });

      this.attachDismissOnClick(this.lastSnackBarRef);
    }, timeout);

    const uid = this.settingService.getOriginalUidFromLocalstorage();
    const logPayload = {
      title,
      toastText,
      uid,
      contextData: options.contextData,
      diagnostics: diagnostics.logData,
    };

    console.error('[importantError]', logPayload);
    this.rollbar.error(`${title} , uid: ${uid}`, logPayload);

    return toastText;
  }

  public showIconPopupNotification(iconName?: string, text?: string): void {
    const dialogConfig: { data: iconNotificationPopupData } = {
      data: {
        matIconName: iconName || 'checkbox-thin-icon',
        notificationText: text || 'Copied to clipboard'
      }
    };
    this.dialog.open(PopupIconNotificationComponent, dialogConfig);
  }

  public copyLink(linkUrl: string): void {
    const performPostCopy = () => {
      this.showImportantSnackBar('Link copied to clipboard', 100, 1000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(linkUrl).then(() => {
        performPostCopy();
      }).catch(() => {
        // fallback to execCommand if writeText fails
        const textArea = document.createElement('textarea');
        textArea.value = linkUrl;
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.position = 'fixed';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          performPostCopy();
        } catch (err) {
          console.error('Copy failed', err);
        }
        document.body.removeChild(textArea);
      });
    } else {
      // fallback for Safari: synchronous copy with execCommand
      const textArea = document.createElement('textarea');
      textArea.value = linkUrl;
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        performPostCopy();
      } catch (err) {
        console.error('Copy failed', err);
      }
      document.body.removeChild(textArea);
    }
  }

  public copyTextToClipboard(text: string, successMessage: string = 'Copied to clipboard.'): void {
    const performPostCopy = () => {
      this.showSnackBar(successMessage, 1800);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        performPostCopy();
      }).catch(() => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.position = 'fixed';
        document.body.appendChild(textArea);
        textArea.select();
        try { document.execCommand('copy'); performPostCopy(); } catch (err) { console.error('Copy failed', err); }
        document.body.removeChild(textArea);
      });
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';
      document.body.appendChild(textArea);
      textArea.select();
      try { document.execCommand('copy'); performPostCopy(); } catch (err) { console.error('Copy failed', err); }
      document.body.removeChild(textArea);
    }
  }

  public generateHref(): string {
    return `mailto:support@3dway.io`
  }

  public logContactUsClick(): void {
    if(environment.type === 'web') {
      navigator.clipboard.writeText('support@3dway.io').then(() => {
        this.showImportantSnackBar('3DWay support email address copied to clipboard!', 100, 1000);
      })
    }
    this.rollbarInfo(`User have clicked contact us button: ${this.settingService.getOriginalUidFromLocalstorage()}`);
  }

  public rollbarInfo(text: string, includeDebugData?: boolean): void {
    if(includeDebugData) {
      text = `${text} , uid: ${this.settingService.getOriginalUidFromLocalstorage()}}`;
    }
    this.rollbar.info(text);
  }

  public rollbarError(text: string): void {
    this.rollbar.error(text);
  }

//   {
//     "cardAuthor": "enJPh5bm30RYp4FTVG4EYReIpub2",
//     "cardCategory": "undefined",
//     "cardContentMobile": "undefined",
//     "cardDescription": "The mecha is co-piloted by two different players, the first serving as a pilot, and the second serving as a gunner.",
//     "cardPosterMobile": "",
//     "cardPrice": 0,
//     "cardTitle": "testxyz1705318534448",
//     "cardType": "model",
//     "id": "testxyz1705318534448",
//     "loadProgress": "0",
//     "loadStatus": "modelDownload",
//     "modifiedDate": "Mon Jan 15 2024 15:35:43 GMT+0400 (Georgia Standard Time)",
//     "purchaseStatus": "uploaded",
//     "warning": ""
// }

  // THIS FORMAT SHOULD BE SYNCHRONIZED WITH YAHOR !!!!!
  public convertFirebaseDtoToGroupedCard(source: FirebaseModelDto): IGroupedCard {
    return {
      id: source.model ? source.model : source.id,
      linkedCollectionId: source.parentCollectionId === 'undefined' ? undefined : source.parentCollectionId,
      previewImageMobile: this.checkUndefinedNullEmpty(source.cardPosterMobile),
      previewVideoMobile: this.checkUndefinedNullEmpty(source.cardContentMobile),
      previewImageWeb: this.checkUndefinedNullEmpty(source.cardPosterWeb),
      previewVideoWeb: this.checkUndefinedNullEmpty(source.cardContentWeb),
      title: source.model ? source.model : source.id,
      fileName: source.model ? source.model : source.id,
      details: source.loadStatus,
      texturesRootPath: source.texturesRootPath ? source.texturesRootPath : '/',
      missingTextures: source.missingTextures ? source.missingTextures : [],
      mappedTextures: source.mappedTextures ? source.mappedTextures : [],
      uploadStatus: {
        status: UPLOAD_STATUS.INPROGRESS,
        progress: source.loadProgress,
        spinnerMode: 'determinate',
        progressMsg: '',
        uploadTaskLink: undefined,
        progressStats: ''
      }
    }
  }

  private checkUndefinedNullEmpty(value: string | undefined | null): string {
    if(value === undefined || value === null || value === '' || value === 'undefined') {
      return undefined;
    }
    return value
  }

  public generateMockedUploadItem(lastFileName: string, uploadTask?: UploadTask ): IGroupedCard {
    return {
        id: lastFileName,
        previewImageMobile: undefined,
        previewVideoMobile: undefined,
        previewImageWeb: undefined,
        previewVideoWeb: undefined,
        title: lastFileName,
        fileName: lastFileName,
        details: 'Private · Uploading... ',
        modifiedDate: Math.floor(Date.now() / 1000),
        uploadStatus: {
          status: UPLOAD_STATUS.INPROGRESS,
          progress: 0,
          spinnerMode: 'determinate',
          progressMsg: 'Private · Uploading... ',
          progressStats: '',
          uploadTaskLink: uploadTask,
        }
    }
  }

  public createEmptyCollection(collectionName: string): ICardCollection {

    const tempId = window.btoa(Math.random().toString().slice(0,6));
    const emptyCollection: ICardCollection = {
      collectionId: tempId,
      collectionSharingId: undefined,
      collectionTitle: collectionName,
      collectionCards: [],
      accessType: 'Private',
      created: new Date(Date.now()).toISOString(),
      updated: new Date(Date.now()).toISOString()
    }
    return emptyCollection;
  }

  public uploadServiceNotification(text: string, duration: number, buttonOktext?: string): MatSnackBarRef<TextOnlySnackBar> {
    return this._snackBar.open(`${text}`, buttonOktext, {
      horizontalPosition: 'start',
      verticalPosition: 'bottom',
      duration,
      panelClass: 'stopupload-snackbar' // use default style for action button
    });
  }  public uploadServiceNotificationFromComponent<T>(text: string, duration: number, buttonOktext: string, component: ComponentType<T>, data?: any): MatSnackBarRef<T> {
    return this._snackBar.openFromComponent(component, {
      data: data || { timeLeft: 4 },
      duration,
      panelClass: 'custom-snackbar'
    });
  }
  public async pickFile(): Promise<File | null> {
    try {
      const result = await FilePicker.pickFiles({
        multiple: false,
        types: ['model/vnd.pixar.usdz', 'model/vnd.usdz+zip'],
        readData: true,
      });

      if (result && result.files && result.files.length > 0) {
        const file = result.files[0];
        return this.createWebAPIFile(file);
      }
    } catch (error) {
      console.error('Error picking file:', error);
    }
    return null;
  }

  private base64Decode(data: string): Uint8Array | null {
    try {
      const byteString = atob(data);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const intArray = new Uint8Array(arrayBuffer);

      for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i);
      }

      return intArray;
    } catch (error) {
      console.error('Error decoding Base64:', error);
      return null;
    }
  }

  public createWebAPIFile(file: any): globalThis.File | null {
    if (!file.data) {
      console.error('File data is not available');
      return null;
    }

    const intArray = this.base64Decode(file.data);

    if (!intArray) {
      console.error('Failed to decode Base64 data');
      return null;
    }

    const arrayBuffer = new ArrayBuffer(intArray.byteLength);
    new Uint8Array(arrayBuffer).set(intArray);
    return new globalThis.File([arrayBuffer], file.name, { type: file.mimeType });
  }

  private sanitizeFileName(originalName: string): string {
    let sanitized = originalName.replace(/[\\/]+/g, '_').trim();
    sanitized = sanitized.replace(/[:]+/g, '');
    sanitized = sanitized.replace(/^[.]+/, '');
    if (!sanitized) {
      sanitized = `untitled_${Date.now()}`;
    }
    return sanitized;
  }

  // modifyFileNameAndPath(fileName: string): string {
  //   const safeName = this.sanitizeFileName(fileName);
  //   const fileParts = safeName.split('.');
  //   const fileExtension = '.' + fileParts.pop();

  //   if (ALLOWED_MODELS_EXTENSIONS.includes(fileExtension)) {
  //     const filePostfix = new Date().getTime().toString();
  //     const newName = fileParts.join('.').replace(/\s+/g, '') + filePostfix + fileExtension;
  //     return newName;
  //   }
  //   return safeName;
  // }

    modifyFileNameAndPathNotSafe(fileName: string): string {
    const fileParts = fileName.split('.');
    const extensionPart = fileParts.pop() || '';
    const fileExtension = '.' + extensionPart;
    const fileExtensionLower = '.' + extensionPart.toLowerCase();
    const isAllowedExtension = ALLOWED_MODELS_EXTENSIONS.some((allowedExt) => {
      return allowedExt.toLowerCase() === fileExtensionLower;
    });
    if (isAllowedExtension) {
      const filePostfix = new Date().getTime().toString();
      const newName = fileParts.join('.') + filePostfix + fileExtension;
      return newName;
    }
    return fileName;
  }

  // Keep it here just in case for debug use somewhere in future

  // private generateLongCollectionFeed(sourceFeed: ICardCollection[]): ICardCollection[] {
  //   if(this.settingsService.feedMultiplayerValue === undefined || isNaN(this.settingsService.feedMultiplayerValue) || this.settingsService.feedMultiplayerValue <= 0 ) {
  //     console.error('Something wrong with _feedMultiplayerValue_: ', this.settingsService.feedMultiplayerValue, ' , return default feed')
  //     return sourceFeed
  //   }
  //   const longFeed = [];
  //   sourceFeed.forEach((el)=>{
  //     longFeed.push(el);
  //   })

  //   for (let index = 0; index < this.settingsService.feedMultiplayerValue - 1; index++) {
  //     sourceFeed.forEach(arrEl => {
  //       const synteticCard: ICardCollection = JSON.parse(JSON.stringify(arrEl));
  //       synteticCard.collectionTitle = arrEl.collectionTitle + ` Clone ${index}`;
  //       synteticCard.collectionContentMobile = arrEl.collectionContentMobile + `?=${index}`;
  //       longFeed.push(synteticCard);
  //     });
  //   }
  //   return longFeed
  // }

   public notImplemented(custom: string = ''): void {
     if(custom) {
      this.showSnackBar(custom, 0);
     } else {
      this.showSnackBar('Feature not implemented yet, we are working on it !', 0);
     }
  }

  // private setMockCollectionCardsForEmptyCols(col: ICardCollection[]): ICardCollection[] {
  //   col.forEach(collection => {
  //     if(collection?.collectionCards.length === 0) {
  //       collection.collectionCards = JSON.parse(JSON.stringify(this.mockDataService.modelCardsMockSourceShort));
  //       const leng = Math.floor(Math.random() * (9 - 1) + 1);
  //       collection.collectionCards.length = leng;
  //     }
  //   });
  //   return col
  // }

  public setPropsForCollectionDetailsBasedOnCard(card: ICard, collection?: ICardCollection): ICardCollection {
    const resultCollection = [card].map((cardItem) => {
      return {
        collectionId: cardItem.id, // because it's collection ! (not card)
        collectionSharingId: cardItem.cardSharingId,
        collectionTitle: cardItem.cardTitle,
        collectionDescription: cardItem.cardDescription,
        collectionPosterMobile: cardItem.cardPosterMobile,
        collectionContentMobile: cardItem.cardContentMobile,
        collectionAuthor: cardItem.cardAuthor,
        collectionCards: collection?.collectionCards ? collection.collectionCards : [],
        showFullscreenBackgroundPreview: false,
        preloadSettings: collection?.preloadSettings ? collection.preloadSettings : undefined,
        // REMOVE LATER REFACTOR HERE:
        created: cardItem.modifiedDate,
        updated: cardItem.modifiedDate,
      }
    })
    return resultCollection[0]
  }

  public generateMockCollection(): ICardCollection {
    return {
      collectionId: window.btoa(Math.random().toString().slice(0,6)),
      collectionSharingId: undefined,
      collectionTitle: '',
      collectionDescription: '',
      collectionPosterMobile: undefined,
      collectionContentMobile: undefined,
      collectionAuthor: undefined,
      collectionCards: [],
      showFullscreenBackgroundPreview: false,
      preloadSettings: undefined,
      created: new Date(Date.now()).toISOString(),
      updated: new Date(Date.now()).toISOString(),
    }
  }

public rollbarErrorThrottlerForModel(errorType: string, modelId: string, errorDetails?: string): void {
  if(errorType && modelId) {
    const errorKey = `${errorType}_${modelId}`;
    const errorTimestamp = sessionStorage.getItem(errorKey);

    if (!errorTimestamp || (Date.now() - parseInt(errorTimestamp, 10)) > 86400000) { // 1 day threshold
      sessionStorage.setItem(errorKey, Date.now().toString());
      this.rollbar.error(`Error Type: ${errorType}, Model ID: ${modelId}, Details: ${errorDetails}`);
    }
  }
}


  calculateIframeDimensions(isVertical: boolean = false): { iframeWidth: string; iframeHeight: string } {
    let iframeHeight: string;
    let iframeWidth: string;

    if (isVertical) {
      // Vertical video (9:16 aspect ratio) - optimized for mobile
      const maxHeight = window.innerHeight * 0.85;
      const aspectRatio = 9 / 16;
      const calculatedWidth = maxHeight * aspectRatio;
      const maxWidth = Math.min(calculatedWidth, window.innerWidth * 0.9);

      iframeWidth = maxWidth + 'px';
      iframeHeight = (maxWidth / aspectRatio) + 'px';

      // Ensure height doesn't exceed viewport
      if (parseFloat(iframeHeight) > maxHeight) {
        iframeHeight = maxHeight + 'px';
        iframeWidth = (maxHeight * aspectRatio) + 'px';
      }
    } else {
      // Horizontal video (16:9 aspect ratio) - original logic
      const maxWidth = 1660;
      const maxViewportWidth = window.innerWidth * 0.8;
      const viewportWidth = Math.min(window.innerWidth - 20, maxWidth, maxViewportWidth);
      const maxViewportHeight = window.innerHeight * 0.8;
      const aspectRatio = 16 / 9;

      if (maxViewportHeight * aspectRatio <= viewportWidth) {
        iframeHeight = maxViewportHeight + 'px';
        iframeWidth = (maxViewportHeight * aspectRatio) + 'px';
      } else {
        iframeWidth = viewportWidth + 'px';
        iframeHeight = (viewportWidth / aspectRatio) + 'px';
      }
    }

    console.log('this.iframeWidth ', iframeWidth, 'this.iframeHeight ', iframeHeight, 'isVertical:', isVertical)
    return { iframeWidth, iframeHeight }
  }

  public removeTimestampFromString(modelIdWithTimestamp: string): string {
    if (!modelIdWithTimestamp) {
      return modelIdWithTimestamp;
    }

    const match = modelIdWithTimestamp.match(/^(.*?)(\d{13})$/);
    if (!match) {
      return modelIdWithTimestamp;
    }
    const baseName = match[1];
    return baseName || modelIdWithTimestamp;
  }

  public detectMobileDevice(): 'android' | 'ios' | null {
    const userAgent = navigator.userAgent.toLowerCase();

    // Check for Android
    if (userAgent.includes('android')) {
      return 'android';
    }

    // Check for iOS
    // EXPIREMENTAL !!!!!!!!!!
    // Modern iPads (iPadOS 13+) pretend to be Macs. We can detect them by checking for Mac platform and touch capability.
    const isModernIPad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

    if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod') || isModernIPad) {
      return 'ios';
    }

    return null;
  }

  public downloadFile(url: string, filename: string): void {
    const anchor = Object.assign(document.createElement('a'), { href: url, download: filename });
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  }

  private attachDismissOnClick(snackBarRef?: MatSnackBarRef<TextOnlySnackBar>): void {
    snackBarRef?.afterOpened().subscribe(() => {
      const snackElement = document.querySelector('.like-snackbar');

      if (snackElement) {
        snackElement.addEventListener('click', () => {
          snackBarRef.dismiss();
        }, { once: true });
      }
    });
  }

  private extractErrorDiagnostics(
    error: unknown,
    fallback: string = 'Unable to complete the request. Please try again.',
  ): { displayMessage: string; logData: Record<string, unknown> } {
    if (error instanceof HttpErrorResponse) {
      const payload = this.asRecord(error.error);
      const nestedError = this.asRecord(payload.error);
      const status = error.status || undefined;
      const statusText = this.pickReadableString(error.statusText);
      const code = this.pickReadableString(nestedError.code, payload.code);
      const message = this.pickReadableString(nestedError.message, payload.message, error.message);
      const details = this.pickReadableString(nestedError.details, payload.details);
      const reason = status === 0
        ? 'Network request failed before the server responded.'
        : this.pickReadableString(details, message, fallback);

      return {
        displayMessage: this.truncateText(
          [
            status ? `HTTP ${status}` : 'HTTP request failed',
            statusText,
            code ? `code=${code}` : undefined,
            reason,
          ].filter(Boolean).join(' | '),
          700,
        ),
        logData: {
          type: 'HttpErrorResponse',
          status,
          statusText,
          url: error.url || undefined,
          code,
          message,
          details,
          payload: Object.keys(payload).length > 0 ? payload : undefined,
        },
      };
    }

    if (error instanceof Error) {
      return {
        displayMessage: this.truncateText(error.message || fallback, 700),
        logData: {
          type: error.name || 'Error',
          message: error.message || undefined,
          stack: error.stack || undefined,
        },
      };
    }

    if (typeof error === 'string' && error.trim()) {
      return {
        displayMessage: this.truncateText(error.trim(), 700),
        logData: {
          type: 'string',
          value: error.trim(),
        },
      };
    }

    if (typeof error === 'number' || typeof error === 'boolean') {
      return {
        displayMessage: this.truncateText(String(error), 700),
        logData: {
          type: typeof error,
          value: error,
        },
      };
    }

    const serialized = this.safeSerialize(error);
    return {
      displayMessage: this.truncateText(serialized || fallback, 700),
      logData: {
        type: typeof error,
        value: serialized,
      },
    };
  }

  private asRecord(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }

    return {};
  }

  private pickReadableString(...values: unknown[]): string | undefined {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }
    }

    return undefined;
  }

  private truncateText(value: string, maxLength: number): string {
    if (!value || value.length <= maxLength) {
      return value;
    }

    return `${value.slice(0, maxLength - 3)}...`;
  }

  private safeSerialize(value: unknown): string | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return error instanceof Error ? error.message : 'Unable to serialize error payload.';
    }
  }


}
